import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Plus, RotateCcw, Trash2, Upload as UploadIcon } from "lucide-react";
import { API, api, download, errMsg } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { ConfirmDialog, Field, PageHeader, StateSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function BusinessTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/business").then((r) => setForm(r.data)).catch(() => {}); }, []);
  if (!form) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/business", form);
      setForm(r.data);
      toast.success("Business settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };
  const F = ({ k, label, type = "text", hint, span }) => (
    <div className={span ? "sm:col-span-2" : ""}>
      <Field label={label} hint={hint}>
        <Input type={type} value={form[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)}
          className="bg-white border-[#E5E0D8]" data-testid={`settings-${k}`} />
      </Field>
    </div>
  );
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-6 space-y-6" data-testid="settings-business">
      <div>
        <h3 className="font-display font-semibold mb-3">Legal & Contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F k="legal_name" label="Legal Business Name" />
          <F k="trade_name" label="Trade Name" />
          <F k="gstin" label="GSTIN" hint="Leave blank until registered — required before GST filing" />
          <F k="pan" label="PAN" />
          <div className="sm:col-span-2"><Field label="Business Address">
            <Textarea value={form.address || ""} onChange={(e) => set("address", e.target.value)}
              className="bg-white border-[#E5E0D8]" data-testid="settings-address" /></Field></div>
          <F k="city" label="City" />
          <Field label="State"><StateSelect value={form.state} onChange={(v) => set("state", v)} testid="settings-state" /></Field>
          <F k="pin" label="PIN Code" />
          <F k="phone" label="Phone" />
          <F k="email" label="Email" />
          <F k="website" label="Website" />
        </div>
      </div>
      <div>
        <h3 className="font-display font-semibold mb-3">Bank & UPI (printed on invoices)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F k="bank_name" label="Bank Name" />
          <F k="account_number" label="Account Number" />
          <F k="ifsc" label="IFSC" />
          <F k="upi_id" label="UPI ID" hint="Enables Scan-to-Pay QR on premium invoices" />
        </div>
      </div>
      <div>
        <h3 className="font-display font-semibold mb-3">Invoice Terms</h3>
        <Textarea rows={4} value={form.terms || ""} onChange={(e) => set("terms", e.target.value)}
          className="bg-white border-[#E5E0D8]" data-testid="settings-terms" />
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="settings-save">
        {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save Settings</Button>
    </div>
  );
}

function GstTab() {
  const [rates, setRates] = useState([]);
  const [sacs, setSacs] = useState([]);
  const [newRate, setNewRate] = useState("");
  const [newSac, setNewSac] = useState({ code: "", description: "", default_rate: 5 });
  const [form, setForm] = useState(null);
  const { user: authUser } = useAuth();
  const writable = can(authUser?.role, "settings", true);
  const load = () => {
    api.get("/settings/tax-rates").then((r) => setRates(r.data)).catch(() => {});
    api.get("/settings/sac-codes").then((r) => setSacs(r.data)).catch(() => {});
    api.get("/settings/business").then((r) => setForm(r.data)).catch(() => {});
  };
  useEffect(load, []);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="settings-gst">
      <div className="bg-[#C9A227]/10 border border-[#C9A227]/40 rounded-xl p-4 text-sm text-[#8a6d14] lg:col-span-2">
        ⚖ <b>CA Review Notice:</b> GST configuration should be reviewed with your CA/tax professional before actual filing.
        Rates and rules here drive every calculation in the system.
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold mb-3">GST Rates</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {rates.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1.5 bg-[#0D4F4A]/5 border border-[#0D4F4A]/20 rounded-full px-3 py-1 text-sm font-semibold text-[#0D4F4A]">
              {r.rate}%{writable && <button onClick={async () => { await api.delete(`/settings/tax-rates/${r.id}`); load(); }} className="text-red-500"><Trash2 className="h-3 w-3" /></button>}
            </span>
          ))}
        </div>
        {writable && (
          <div className="flex gap-2">
            <Input type="number" placeholder="New rate %" value={newRate} onChange={(e) => setNewRate(e.target.value)}
              className="w-32 bg-white border-[#E5E0D8]" data-testid="settings-new-rate" />
            <Button size="sm" className="bg-[#0D4F4A]" data-testid="settings-add-rate"
              onClick={async () => { if (newRate === "") return; await api.post("/settings/tax-rates", { rate: Number(newRate) }); setNewRate(""); load(); }}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
          </div>
        )}
        {form && (
          <div className="mt-5">
            <Field label="Default GST Rate (new items)">
              <Select value={String(form.default_gst_rate)} onValueChange={async (v) => {
                await api.put("/settings/business", { default_gst_rate: Number(v) });
                toast.success("Default rate updated"); load();
              }}>
                <SelectTrigger className="w-40 bg-white border-[#E5E0D8]" data-testid="settings-default-rate"><SelectValue /></SelectTrigger>
                <SelectContent>{rates.map((r) => <SelectItem key={r.id} value={String(r.rate)}>{r.rate}%</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        )}
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold mb-3">SAC / HSN Codes</h3>
        <ul className="space-y-1.5 mb-4 max-h-56 overflow-y-auto">
          {sacs.map((s) => (
            <li key={s.id} className="flex items-center justify-between text-sm border-b border-[#E5E0D8]/60 pb-1.5">
              <span><b className="num">{s.code}</b> · {s.description} <span className="text-xs text-[#5C6B69]">({s.default_rate}%)</span></span>
              {writable && <button onClick={async () => { await api.delete(`/settings/sac-codes/${s.id}`); load(); }} className="text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
            </li>
          ))}
        </ul>
        {writable && (
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Code" value={newSac.code} onChange={(e) => setNewSac({ ...newSac, code: e.target.value })}
              className="bg-white border-[#E5E0D8]" data-testid="settings-new-sac-code" />
            <Input placeholder="Description" value={newSac.description} onChange={(e) => setNewSac({ ...newSac, description: e.target.value })}
              className="bg-white border-[#E5E0D8]" data-testid="settings-new-sac-desc" />
            <Button size="sm" className="bg-[#0D4F4A]" data-testid="settings-add-sac"
              onClick={async () => { if (!newSac.code) return; await api.post("/settings/sac-codes", newSac); setNewSac({ code: "", description: "", default_rate: 5 }); load(); }}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceSettingsTab() {
  const [form, setForm] = useState(null);
  const [fys, setFys] = useState([]);
  const { user: authUser } = useAuth();
  const writable = can(authUser?.role, "settings", true);
  useEffect(() => {
    api.get("/settings/business").then((r) => setForm(r.data)).catch(() => {});
    api.get("/gst/fy-options").then((r) => setFys(r.data)).catch(() => {});
  }, []);
  if (!form) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const set = async (patch) => {
    try {
      const r = await api.put("/settings/business", patch);
      setForm(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const TEMPLATES = [
    { key: "classic", name: "Template A — Classic Corporate", desc: "Dark green header, full bordered grid, traditional layout" },
    { key: "minimal", name: "Template B — Modern Minimal", desc: "Clean lines, generous whitespace, gold accents" },
    { key: "premium", name: "Template C — Premium Dreamzz", desc: "Branded band, gold rule, UPI scan-to-pay QR" },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="settings-invoice">
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5 space-y-4">
        <h3 className="font-display font-semibold">Numbering & Financial Year</h3>
        <div className="grid grid-cols-2 gap-3">
          {[["invoice_prefix", "Invoice Prefix"], ["credit_note_prefix", "Credit Note Prefix"],
            ["debit_note_prefix", "Debit Note Prefix"], ["quotation_prefix", "Quotation Prefix"],
            ["receipt_prefix", "Receipt Prefix"], ["proforma_prefix", "Proforma Prefix"]].map(([k, l]) => (
            <Field key={k} label={l}>
              <Input value={form[k] || ""} disabled={!writable} className="bg-white border-[#E5E0D8]"
                onChange={(e) => setForm({ ...form, [k]: e.target.value.toUpperCase() })}
                onBlur={() => writable && set({ [k]: form[k] })} data-testid={`settings-${k}`} />
            </Field>
          ))}
          <Field label="Starting Number">
            <Input type="number" value={form.invoice_start || 1} disabled={!writable}
              className="bg-white border-[#E5E0D8]" onChange={(e) => setForm({ ...form, invoice_start: Number(e.target.value) })}
              onBlur={() => writable && set({ invoice_start: form.invoice_start })} data-testid="settings-invoice-start" />
          </Field>
          <Field label="Active Financial Year" hint="April → March. Drives numbering & reports.">
            <Select value={form.active_fy} disabled={!writable} onValueChange={(v) => set({ active_fy: v })}>
              <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="settings-active-fy"><SelectValue /></SelectTrigger>
              <SelectContent>{fys.map((f) => <SelectItem key={f.fy} value={f.fy}>FY {f.fy} ({f.from} → {f.to})</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <p className="text-xs text-[#5C6B69]">Format: <b className="num">{form.invoice_prefix}/{form.active_fy?.slice(2, 4)}-{form.active_fy?.slice(7, 9)}/0001</b> · Sequential, duplicate-proof.</p>
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold mb-3">Default Invoice Template</h3>
        <div className="space-y-2">
          {TEMPLATES.map((t) => (
            <label key={t.key} className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${form.invoice_template === t.key ? "border-[#0D4F4A] bg-[#0D4F4A]/5" : "border-[#E5E0D8]"}`}
              data-testid={`template-option-${t.key}`}>
              <input type="radio" name="template" checked={form.invoice_template === t.key} disabled={!writable}
                onChange={() => set({ invoice_template: t.key })} className="mt-1 accent-[#0D4F4A]" />
              <span>
                <span className="block text-sm font-semibold">{t.name}</span>
                <span className="block text-xs text-[#5C6B69]">{t.desc}</span>
              </span>
            </label>
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-4" data-testid="template-preview-button"
          onClick={async () => {
            const r = await api.get("/invoices?limit=1&status=paid");
            const inv = r.data.items[0] || (await api.get("/invoices?limit=1")).data.items[0];
            if (!inv) return toast.info("Create an invoice first to preview templates");
            window.open(`${API}/invoices/${inv.id}/pdf?template=${form.invoice_template}&download=false`, "_blank");
          }}>
          Preview with a real invoice</Button>
      </div>
    </div>
  );
}

function ImportCard({ entity, label }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/import/${entity}`, fd);
      setResult(r.data);
      toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="border border-[#E5E0D8] rounded-lg p-3" data-testid={`import-${entity}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold">{label}</span>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => download(`/import/template/${entity}`, `${entity}_template.csv`)}
            data-testid={`import-${entity}-template`}><Download className="h-3 w-3 mr-1" />Template</Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv" className="hidden" onChange={upload} data-testid={`import-${entity}-file`} />
            <span className="inline-flex items-center h-7 px-2 rounded-md text-xs font-semibold bg-[#0D4F4A] text-white hover:bg-[#136A63]">
              {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <UploadIcon className="h-3 w-3 mr-1" />}Upload CSV</span>
          </label>
        </div>
      </div>
      {result && (
        <div className="text-xs text-[#5C6B69]" data-testid={`import-${entity}-result`}>
          <span className="text-emerald-700 font-semibold">{result.created} created</span> ·{" "}
          <span className="text-sky-700 font-semibold">{result.updated} updated</span> ·{" "}
          <span>{result.skipped} skipped</span>
          {result.errors?.length > 0 && (
            <ul className="mt-1 text-amber-700">
              {result.errors.slice(0, 5).map((er, i) => <li key={i}>Row {er.row} ({er.name}): {er.message}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function DataTab() {
  const { user } = useAuth();
  const [confirmReset, setConfirmReset] = useState(false);
  const writable = can(user?.role, "backup", true);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="settings-data">
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold mb-2">Backup / Full Export</h3>
        <p className="text-sm text-[#5C6B69] mb-4">Download all business data (customers, bookings, invoices, payments, expenses, ledgers, settings) as JSON. Keep a copy monthly.</p>
        <Button variant="outline" disabled={!writable} data-testid="backup-download"
          onClick={() => download("/backup", "dreamzz-backup.json").catch((e) => toast.error(errMsg(e)))}>
          <Download className="h-4 w-4 mr-1" /> Download Full Backup</Button>
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5 lg:col-span-2">
        <h3 className="font-display font-semibold mb-2">Bulk Import (CSV)</h3>
        <p className="text-sm text-[#5C6B69] mb-4">Download a template, fill it in Excel/Sheets, and upload. Existing records are matched (GSTIN/phone for customers, name for services/vendors) and updated instead of duplicated.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ImportCard entity="customers" label="Customers" />
          <ImportCard entity="agents" label="B2B Agents" />
          <ImportCard entity="services" label="Services / Packages" />
          <ImportCard entity="vendors" label="Vendors" />
        </div>
      </div>
      <div className="bg-white border border-red-200 rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold mb-2 text-red-700">Demo Data</h3>
        <p className="text-sm text-[#5C6B69] mb-4">Remove all records marked DEMO (customers, agents, bookings, invoices, payments, expenses, ledger entries). Your real records stay untouched.</p>
        <Button variant="outline" disabled={!writable} className="border-red-300 text-red-600"
          onClick={() => setConfirmReset(true)} data-testid="demo-reset-button">
          <RotateCcw className="h-4 w-4 mr-1" /> Reset Demo Data</Button>
        <ConfirmDialog open={confirmReset} onClose={() => setConfirmReset(false)} testid="demo-reset-confirm"
          title="Delete all demo data?" description="This permanently removes every record flagged as demo. Financial audit logs are preserved."
          onConfirm={async () => {
            try {
              const r = await api.post("/demo/reset");
              toast.success(`Demo data removed (${Object.values(r.data.removed).reduce((a, b) => a + b, 0)} records)`);
              setConfirmReset(false);
            } catch (e) {
              toast.error(errMsg(e));
            }
          }} />
      </div>
    </div>
  );
}

export default function Settings() {
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Business profile, GST configuration, numbering, templates & data safety"
        testid="settings-header" />
      <Tabs defaultValue="business">
        <TabsList className="bg-white border border-[#E5E0D8] rounded-xl p-1.5 mb-5" data-testid="settings-tabs">
          <TabsTrigger value="business" data-testid="settings-tab-business">Business Profile</TabsTrigger>
          <TabsTrigger value="gst" data-testid="settings-tab-gst">GST & Tax Rates</TabsTrigger>
          <TabsTrigger value="invoice" data-testid="settings-tab-invoice">Invoice & Numbering</TabsTrigger>
          <TabsTrigger value="data" data-testid="settings-tab-data">Data & Backup</TabsTrigger>
        </TabsList>
        <TabsContent value="business"><BusinessTab /></TabsContent>
        <TabsContent value="gst"><GstTab /></TabsContent>
        <TabsContent value="invoice"><InvoiceSettingsTab /></TabsContent>
        <TabsContent value="data"><DataTab /></TabsContent>
      </Tabs>
    </div>
  );
}
